import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { customerApi, formatApiError, CUSTOMER_TOKEN_KEY } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Check, ChevronRight, IdCard, Stethoscope, Search, Lock, Sparkles, AlertTriangle } from "lucide-react";
import Logo from "@/components/layout/Logo";
import { MEDICAL_QUESTIONS, COSMETIC_INGREDIENTS } from "@/lib/medicalQuestionnaire";
// "Break-the-Glass" Part 2 (Iter-300): every rule that gates the guest
// check-in wizard — step validation, canonical Nationality / Country /
// email / E.164 WhatsApp lookup, medical / massage preview→state
// mapping — lives inside `CheckInEngine.jsx`. This file must NEVER
// re-implement any of that logic inline. See SYSTEM_RULES.md.
import {
  HEAR_OPTIONS,
  GENDER_OPTIONS,
  NationalityCombobox,
  CountryCombobox,
  CheckinPhoneInput,
  extractProfileFromPreview,
  extractMedicalFromPreview,
  extractMassageFromPreview,
  withMedicalAnswer,
  withMedicalNote,
  validateStep1,
  validateStep2Massage,
  computeLastStep,
  isValidEmail,
  isValidWhatsappE164,
} from "@/lib/CheckInEngine";
import MassagePreferenceStep from "./MassagePreferenceStep";

/**
 * /portal/checkin?cid=…&token=…
 *
 * Unified Front-Desk Check-In wizard restructured to TWO steps (Feb 2026):
 *   Step 1 — Profile & Terms  (Identity + Terms tick, NO password — Iter-362)
 *   Step 2 — Clinical Medical Questionnaire  (auto-skipped by Smart Medical
 *            Screening for returning guests or massage-only bookings)
 *
 * Iter-362 · Passwordless Front-Desk Check-In. The `New password` +
 * `Confirm password` fields were REMOVED entirely — guests now sign
 * into their portal via the email-OTP surface at `/portal/login`
 * (Iter-358). `agree_terms` still gates activation (legal capture).
 *
 * On Finish the guest is flipped to Active Member and every one of today's
 * live appointments is stamped with `checked_in_at`, turning the calendar
 * tile bright teal.
 */

// `HEAR_OPTIONS` + `GENDER_OPTIONS` are exported from
// `@/lib/CheckInEngine` — every check-in surface shares the same
// source list + intake policy.

export default function CustomerCheckIn() {
  const nav = useNavigate();
  const { setCustomerFromToken } = useAuth();
  const [params] = useSearchParams();
  const cid = params.get("cid") || "";
  const token = params.get("token") || "";
  const isTablet = params.get("tablet") === "1";
  const [done, setDone] = useState(false); // Kiosk "thank you" screen.

  const [checking, setChecking] = useState(true);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);

  const skipMedical = !!preview?.skip_medical;
  const skipReason = preview?.skip_reason || "";
  // Massage Preference Card flag — when the guest has strictly ONE
  // Massage-category appointment today, we swap the medical step out
  // for the Massage Preference Card. Independent of skip_medical so a
  // returning guest still lands on the massage card when they book a
  // single-massage session.
  const useMassageCard = !!preview?.use_massage_card;
  // Returning-guest Medical Review mode (Feb 2026): the profile still
  // needs updating, but Step 2 is a REVIEW of the guest's previous
  // medical answers with a clear "please confirm any changes" header,
  // not a fresh questionnaire. Every field is pre-filled from the last
  // visit's record so the guest only touches what changed.
  const isReviewMode = skipReason === "returning_guest";
  const [step, setStep] = useState(1);

  // Step 1 — Profile & Terms (Iter-362 · Passwordless)
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [gender, setGender] = useState("");
  const [dob, setDob] = useState("");
  const [nationality, setNationality] = useState("");
  const [country, setCountry] = useState("");
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [hearSource, setHearSource] = useState("");
  const [hearLabel, setHearLabel] = useState("");    // human-readable label of the resolved origin
  const [referrerId, setReferrerId] = useState(null);
  const [referrerName, setReferrerName] = useState("");
  // Iter-362 · Passwordless Front-Desk Check-In. Password + confirm
  // fields removed from the iPad flow — guests now sign in via the
  // /portal/login email-OTP surface (Iter-358) whenever they open the
  // portal later. Kept the `agree` (Terms + Privacy) checkbox — that's
  // a legal capture, not an auth gate.
  const [agree, setAgree] = useState(false);

  // Step 2 — Medical
  const [medical, setMedical] = useState({
    medication_allergies: "",
    cosmetic_allergies: [],
    current_medications: "",
    medical_questionnaire: {},
  });
  // Step 2 (alternate) — Massage Preference Card
  const [massage, setMassage] = useState({
    pressure: "",
    mood_physically: 5,
    mood_emotionally: 5,
    avoid_areas: [],
    focus_areas: [],
    health_conditions: {},
    agreed_disclaimer: false,
  });
  const [busy, setBusy] = useState(false);

  // Preview → prefill profile fields so returning guests aren't retyping
  // their name.
  useEffect(() => {
    let cancelled = false;
    if (!cid || !token) {
      setChecking(false);
      setError("This check-in link is missing required information.");
      return;
    }
    (async () => {
      try {
        const r = await customerApi.post("/auth/customer/activate/preview", { customer_id: cid, token });
        if (cancelled) return;
        setPreview(r.data);
        // All preview→state mappers live in `CheckInEngine` so every
        // intake surface stays byte-for-byte identical. The engine's
        // medical mapper deep-clones the payload — mutations here
        // NEVER reach the incoming record (SYSTEM_RULES: 1:1 Exact
        // Mirror + NO automated text injection).
        const profile = extractProfileFromPreview(r.data);
        setFirstName(profile.firstName);
        setLastName(profile.lastName);
        setGender(profile.gender);
        setDob(profile.dob);
        setNationality(profile.nationality);
        setCountry(profile.country);
        setEmail(profile.email);
        setWhatsapp(profile.whatsapp);
        if (profile.hearSource) {
          setHearSource(profile.hearSource);
          setHearLabel(profile.hearLabel);
        }
        // Feb 2026 — Hydrate the referrer attribution from the preview
        // so a repeat guest whose stored profile reads
        // `referral_source: "referral"` arrives with the matching
        // `referrerId` already set. Without this the Step-1 Continue
        // button stays disabled on returning guests (the engine's
        // `validateStep1` requires a `referrerId` whenever the source
        // is `referral`, and the origin field is LOCKED on review mode
        // so the guest can't pick one themselves).
        if (r.data?.referred_by_customer_id) {
          setReferrerId(r.data.referred_by_customer_id);
          setReferrerName(r.data.referred_by_customer_name || "");
        }
        const priorMedical = extractMedicalFromPreview(r.data);
        if (priorMedical) setMedical(priorMedical);
        const priorMassage = extractMassageFromPreview(r.data);
        if (priorMassage) setMassage((prev) => ({ ...prev, ...priorMassage }));
      } catch (err) {
        if (!cancelled) setError(formatApiError(err));
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [cid, token]);

  // Kiosk back-button lockdown (unchanged from previous impl).
  useEffect(() => {
    if (!isTablet) return;
    window.history.pushState({ __kiosk: true }, "", window.location.href);
    const onPop = () => window.history.pushState({ __kiosk: true }, "", window.location.href);
    const onKey = (e) => {
      if (e.key === "Backspace") {
        const t = e.target && e.target.tagName;
        if (t !== "INPUT" && t !== "TEXTAREA") e.preventDefault();
      }
      if (e.altKey && (e.key === "ArrowLeft" || e.key === "ArrowRight")) e.preventDefault();
    };
    const onBeforeUnload = (e) => {
      if (done) return;
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("popstate", onPop);
    window.addEventListener("keydown", onKey);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [isTablet, done]);

  useEffect(() => {
    if (!done || !isTablet) return;
    const t = setTimeout(() => window.location.replace("/staff/login"), 5000);
    return () => clearTimeout(t);
  }, [done, isTablet]);

  // Origin picker (Feb 2026 "Google Trick") — ONE live search input
  // that surfaces both built-in sources (Google / Instagram / Walk-in)
  // AND matching customer names. Debounced 220ms so mobile keyboards
  // stay responsive; the source list is filtered locally (no API
  // needed) and merged above the customer-name results.
  const [originQuery, setOriginQuery] = useState("");
  const [originResults, setOriginResults] = useState([]);
  const [originSearching, setOriginSearching] = useState(false);
  const [originOpen, setOriginOpen] = useState(false);
  const debounceRef = useRef(null);
  useEffect(() => {
    if (hearSource) { setOriginResults([]); return; } // already picked
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = originQuery.trim();
    if (q.length < 1) { setOriginResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setOriginSearching(true);
      try {
        const r = await customerApi.post("/auth/customer/checkin/referrers/search", {
          customer_id: cid, token, query: q,
        });
        setOriginResults(Array.isArray(r.data) ? r.data : []);
      } catch {
        setOriginResults([]);
      } finally {
        setOriginSearching(false);
      }
    }, 220);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [originQuery, hearSource, cid, token]);

  // Locally filter built-in sources by whatever the guest is typing.
  // Case-insensitive substring match. Deliberately simple so "goo"
  // catches "Google search" and "ins" catches "Instagram" instantly.
  const filteredSources = useMemo(() => {
    const q = originQuery.trim().toLowerCase();
    if (!q) return HEAR_OPTIONS;
    return HEAR_OPTIONS.filter((h) => h.label.toLowerCase().includes(q) || h.key.toLowerCase().includes(q));
  }, [originQuery]);

  const clearOrigin = () => {
    setHearSource(""); setHearLabel("");
    setReferrerId(null); setReferrerName("");
    setOriginQuery(""); setOriginResults([]);
  };
  const pickSource = (opt) => {
    setHearSource(opt.key); setHearLabel(opt.label);
    setReferrerId(null); setReferrerName("");
    setOriginOpen(false); setOriginQuery("");
  };
  const pickCustomer = (c) => {
    setHearSource("referral"); setHearLabel(`Referred by ${c.name}`);
    setReferrerId(c.id); setReferrerName(c.name);
    setOriginOpen(false); setOriginQuery("");
  };

  const setAnswer = (key, ans) => setMedical((m) => withMedicalAnswer(m, key, ans));
  const setNote = (key, note) => setMedical((m) => withMedicalNote(m, key, note));

  // Pre-treatment questionnaire starts answered "No" on every row so the
  // guest only taps the ones that actually apply (same pattern as the
  // massage card). Prior answers hydrated from the preview are untouched.
  useEffect(() => {
    setMedical((m) => {
      const mq = m.medical_questionnaire || {};
      const missing = MEDICAL_QUESTIONS.filter((q) => !(mq[q.key] || {}).answer);
      if (!missing.length) return m;
      const next = { ...mq };
      missing.forEach((q) => { next[q.key] = { ...(mq[q.key] || {}), answer: "no" }; });
      return { ...m, medical_questionnaire: next };
    });
  }, [medical]);

  // Feb 2026 — Legacy referral exemption. Returning guests whose
  // stored `referral_source` is `"referral"` but who have no
  // `referred_by_customer_id` on file (imported data, pre-2026
  // flows) end up with a LOCKED origin field they cannot change and
  // no referrer id for the engine's gate to approve. We surface the
  // same locked pill the UI shows, but relax the engine's referrer
  // requirement by re-running the validator against a benign source
  // ("other") — a pure consumer-layer bypass so the engine stays
  // sealed per System Rules.
  const legacyReferralWithoutId = hearSource === "referral" && !referrerId;

  const canStep1 = useMemo(
    () => {
      const strict = validateStep1({
        firstName, lastName, gender, dob,
        nationality, country, email, whatsapp,
        hearSource, referrerId,
        // Iter-362: Passwordless check-in. The engine's `validateStep1`
        // still gates password+confirm on non-review guests for legacy
        // callers; we satisfy it with a stable placeholder pair so the
        // real gate becomes the Terms tick + `agree_terms=true` on the
        // POST body. Empty strings would fail (<6 chars). The engine is
        // locked so we cannot loosen the check inline — this is the
        // narrowest workaround that preserves the engine's invariant.
        password: "otp-flow-placeholder",
        confirm: "otp-flow-placeholder",
        agree, isReviewMode,
      });
      if (strict) return true;
      if (!legacyReferralWithoutId) return false;
      // Legacy referral bypass: everything else must still satisfy
      // the engine — only the referrer gate is loosened.
      return validateStep1({
        firstName, lastName, gender, dob,
        nationality, country, email, whatsapp,
        hearSource: "other", referrerId: null,
        password: "otp-flow-placeholder",
        confirm: "otp-flow-placeholder",
        agree, isReviewMode,
      });
    },
    [firstName, lastName, gender, dob, nationality, country, email, whatsapp, hearSource, referrerId, agree, isReviewMode, legacyReferralWithoutId],
  );

  // Wizard step count is derived by `computeLastStep` inside the
  // engine — kept as a single call so future skip-paths only touch
  // one file.
  const lastStep = computeLastStep({ useMassageCard, isReviewMode, skipMedical });

  // Jun 2026 — "Continue" is always tappable; when something is still
  // missing it scrolls to the FIRST empty field and outlines it in red
  // instead of sitting silently disabled. Purely a locator: the real
  // gate stays the engine's `validateStep1`.
  const [invalidField, setInvalidField] = useState("");
  const step1Fields = useMemo(() => ([
    { key: "first_name", label: "First name", ok: !!firstName.trim() },
    { key: "last_name", label: "Last name", ok: !!lastName.trim() },
    { key: "gender", label: "Gender", ok: !!gender },
    { key: "dob", label: "Date of birth", ok: !!dob },
    { key: "nationality", label: "Nationality", ok: !!(nationality || "").trim() },
    { key: "country", label: "Country of residence", ok: !!(country || "").trim() },
    { key: "email", label: "Email", ok: isValidEmail(email) },
    { key: "whatsapp", label: "WhatsApp", ok: isValidWhatsappE164(whatsapp) },
    { key: "hear", label: "How did you hear about us?", ok: !!hearSource },
    { key: "terms", label: "Terms and Privacy agreement", ok: isReviewMode || agree },
  ]), [firstName, lastName, gender, dob, nationality, country, email, whatsapp, hearSource, agree, isReviewMode]);

  // Clear the red outline as soon as that field is filled in.
  useEffect(() => {
    if (!invalidField) return;
    if (step1Fields.find((f) => f.key === invalidField)?.ok) setInvalidField("");
  }, [invalidField, step1Fields]);

  const ringFor = (key) => (invalidField === key ? "rounded-lg ring-2 ring-rose-500 ring-offset-2 p-2 -m-2" : "");

  // Returning guests: when their health answers were saved on a previous
  // visit, the medical page opens with an amber "please confirm" banner.
  const medicalUpdatedLabel = useMemo(() => {
    const raw = preview?.medical_updated_at;
    if (!raw) return "";
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  }, [preview]);

  // Returns true when step 1 is complete; otherwise parks the guest on
  // the first missing field.
  const focusFirstMissing = () => {
    const bad = step1Fields.find((f) => !f.ok);
    setStep(1);
    if (bad) {
      setInvalidField(bad.key);
      toast.error(`Please complete: ${bad.label}`);
      setTimeout(() => {
        document.getElementById(`checkin-field-${bad.key}`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
    } else {
      toast.error("Please complete every field on the profile step before continuing.");
    }
  };

  const goNext = () => {
    if (step !== 1 || canStep1) { setInvalidField(""); setStep(step + 1); return; }
    focusFirstMissing();
  };

  const canStep2Massage = useMemo(
    () => validateStep2Massage({ useMassageCard, massage }),
    [useMassageCard, massage],
  );

  const submit = async () => {
    if (!canStep1) { focusFirstMissing(); return; }
    if (useMassageCard && !canStep2Massage) {
      toast.error("Please pick a pressure and agree to the Spa Treatment Release before submitting.");
      setStep(2);
      return;
    }
    setBusy(true);
    try {
      const r = await customerApi.post("/auth/customer/checkin/complete", {
        customer_id: cid,
        token,
        // Iter-362 · Passwordless. Endpoint accepts an empty string
        // and IGNORES the field — guests use OTP later.
        new_password: "",
        agree_terms: isReviewMode ? true : agree,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        gender,
        dob,
        nationality: nationality.trim(),
        country: country.trim(),
        email: email.trim(),
        whatsapp: whatsapp.trim(),
        referral_source: hearSource,
        referred_by_customer_id: hearSource === "referral" ? referrerId : null,
        // Review mode always sends the (possibly-edited) medical data
        // so the server can log a "Medical Review Updated" audit entry
        // even when the guest changed nothing. Other skip paths skip
        // the payload entirely. Massage-card path never sends medical.
        medical: (useMassageCard || (skipMedical && !isReviewMode)) ? {} : medical,
        massage_preferences: useMassageCard ? massage : null,
      });
      if (isTablet) {
        setDone(true);
      } else {
        localStorage.setItem(CUSTOMER_TOKEN_KEY, r.data.token);
        await setCustomerFromToken(r.data.user);
        toast.success(`Welcome to Cocoon, ${r.data.user?.first_name || "there"}. You're checked in.`);
        nav("/portal/profile");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const steps = useMassageCard
    ? [
        { n: 1, label: "Profile & Terms", icon: IdCard },
        { n: 2, label: "Massage Preferences", icon: Sparkles },
      ]
    : isReviewMode
      ? [
          { n: 1, label: "Profile & Terms", icon: IdCard },
          { n: 2, label: "Medical Review", icon: Stethoscope },
        ]
      : skipMedical
        ? [{ n: 1, label: "Profile & Terms", icon: IdCard }]
        : [
            { n: 1, label: "Profile & Terms", icon: IdCard },
            { n: 2, label: "Medical", icon: Stethoscope },
          ];
  const skipNotice = skipMedical && !isReviewMode && !useMassageCard
    ? (skipReason === "massage_only_day"
        ? "You're only booked for massage today, so we can skip the medical questionnaire."
        : "The medical questionnaire isn't required for today's visit.")
    : "";

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="checkin-checking">
        <p className="text-sm text-muted-foreground">Verifying your check-in link…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-sm text-center" data-testid="checkin-error">
          <Logo variant="mark" to="/" size={56} />
          <h1 className="font-editorial text-3xl mt-6">Link unavailable</h1>
          <p className="text-sm text-muted-foreground mt-3">{error}</p>
          <p className="text-sm mt-6">Please ask the Cocoon team to resend your check-in link.</p>
        </div>
      </div>
    );
  }
  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[hsl(40_20%_97%)]" data-testid="checkin-thankyou">
        <div className="max-w-sm text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto">
            <Check className="w-8 h-8" />
          </div>
          <h1 className="font-editorial text-3xl mt-6" data-testid="checkin-thankyou-title">Thank you, {firstName || preview?.first_name || "there"}.</h1>
          <p className="text-sm text-muted-foreground mt-3">
            Your check-in is complete. Please hand the tablet back to the front desk.
          </p>
          <p className="text-[11px] text-muted-foreground mt-6" data-testid="checkin-thankyou-redirect">
            Returning to staff sign-in in 5 seconds…
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(40_20%_97%)] py-6 sm:py-10 px-3 sm:px-6" data-testid="checkin-page">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center justify-center mb-4">
          {isTablet
            ? <span data-testid="brand-logo-static" className="inline-flex items-center"><Logo variant="mark" to="/" size={48} className="pointer-events-none" /></span>
            : <Logo variant="mark" to="/" size={48} />}
        </div>
        <Card className="rounded-2xl overflow-hidden">
          <CardContent className="p-5 sm:p-7">
            <p className="tracking-overline text-[10px] text-muted-foreground uppercase">Front-Desk Check-In</p>
            <h1 className="font-editorial text-2xl sm:text-3xl mt-1">
              Hi {firstName || preview?.first_name || "there"}, let's get you checked in.
            </h1>
            <p className="text-xs text-muted-foreground mt-1">
              {useMassageCard
                ? "Two quick pages — profile check + massage preferences."
                : isReviewMode
                  ? "Two quick pages — profile check + medical review."
                  : skipMedical
                    ? "One quick page and you're ready for treatment."
                    : "Two quick pages and you're ready for treatment."}
            </p>
            {skipMedical && !isReviewMode && !useMassageCard && (
              <p
                className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-md px-2.5 py-1.5 mt-3"
                data-testid={`checkin-skip-medical-notice-${skipReason || "generic"}`}
              >
                {skipNotice}
              </p>
            )}

            <ol className="flex items-center gap-1 mt-5 mb-6 overflow-x-auto" data-testid="checkin-stepper">
              {steps.map(({ n, label, icon: Icon }, i) => {
                const active = step === n;
                const doneStep = step > n;
                return (
                  <li key={n} className="flex items-center gap-1 text-[11px] shrink-0">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center ${active ? "bg-primary text-primary-foreground" : doneStep ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"}`}>
                      {doneStep ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                    </span>
                    <span className={active ? "font-medium" : "text-muted-foreground"}>{label}</span>
                    {i < steps.length - 1 && <ChevronRight className="w-3 h-3 text-muted-foreground mx-0.5" />}
                  </li>
                );
              })}
            </ol>

            {step === 1 && (
              <div className="space-y-5" data-testid="checkin-step-1">
                <p className="text-sm">Verify your details so we can look after you properly.</p>

                {/* Identity ------------------------------------------------ */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div id="checkin-field-first_name" className={ringFor("first_name")}>
                    <Label htmlFor="first-name">First name <span className="text-rose-500">*</span></Label>
                    <Input id="first-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required data-testid="checkin-first-name" className="mt-1" />
                  </div>
                  <div id="checkin-field-last_name" className={ringFor("last_name")}>
                    <Label htmlFor="last-name">Last name <span className="text-rose-500">*</span></Label>
                    <Input id="last-name" value={lastName} onChange={(e) => setLastName(e.target.value)} required data-testid="checkin-last-name" className="mt-1" />
                  </div>
                </div>

                <div id="checkin-field-gender" className={ringFor("gender")}>
                  <Label>Gender <span className="text-rose-500">*</span></Label>
                  <div className="grid grid-cols-2 gap-2 mt-1.5">
                    {GENDER_OPTIONS.map((g) => {
                      const active = gender === g.key;
                      return (
                        <button
                          key={g.key}
                          type="button"
                          onClick={() => setGender(g.key)}
                          className={
                            "h-10 rounded-full text-sm font-medium transition-all active:scale-[0.98] " +
                            (active
                              ? "bg-primary text-primary-foreground shadow-sm"
                              : "bg-white border border-border text-foreground hover:bg-muted/60")
                          }
                          aria-pressed={active}
                          data-testid={`checkin-gender-${g.key}`}
                        >
                          {g.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div id="checkin-field-dob" className={ringFor("dob")}>
                    <Label htmlFor="dob">Date of birth <span className="text-rose-500">*</span></Label>
                    <Input id="dob" type="date" value={dob} onChange={(e) => setDob(e.target.value)} required data-testid="checkin-dob" className="mt-1 h-12 text-base" />
                  </div>
                  <div id="checkin-field-nationality" className={ringFor("nationality")}>
                    <Label htmlFor="nationality">Nationality <span className="text-rose-500">*</span></Label>
                    <div className="mt-1">
                      <NationalityCombobox
                        value={nationality}
                        onChange={setNationality}
                        placeholder="Type to search nationality…"
                        testId="checkin-nationality"
                      />
                    </div>
                  </div>
                </div>

                {/* Country of residence (Feb 2026) — separate from
                    Nationality: a French national living in Bali picks
                    "French" + "Indonesia". Both are mandatory; the
                    picker enforces canonical values so ledger reports
                    aren't spoiled by typos. */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div id="checkin-field-country" className={ringFor("country")}>
                    <Label htmlFor="country">Country of residence <span className="text-rose-500">*</span></Label>
                    <div className="mt-1">
                      <CountryCombobox
                        value={country}
                        onChange={setCountry}
                        placeholder="Type to search country…"
                        testId="checkin-country"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact — mandatory (Feb 2026). Guest may correct
                    what the front desk previously guessed at booking. */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div id="checkin-field-email" className={ringFor("email")}>
                    <Label htmlFor="checkin-email">Email <span className="text-rose-500">*</span></Label>
                    <Input
                      id="checkin-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                      inputMode="email"
                      placeholder="you@example.com"
                      data-testid="checkin-email"
                      className="mt-1"
                    />
                  </div>
                  <div id="checkin-field-whatsapp" className={ringFor("whatsapp")}>
                    <Label htmlFor="checkin-whatsapp">WhatsApp <span className="text-rose-500">*</span></Label>
                    {/* SYSTEM_RULES: every phone input MUST use the shared
                        PhoneInput (E.164 output, sticky ISO). The engine
                        re-exports it as `CheckinPhoneInput` so this
                        surface stays wired to the single source of
                        truth. Country flag stays global (Bahrain +973,
                        Italy +39, UK +44, etc.). */}
                    <div className="mt-1">
                      <CheckinPhoneInput
                        value={whatsapp}
                        onChange={setWhatsapp}
                        testId="checkin-whatsapp"
                      />
                    </div>
                  </div>
                </div>

                {/* Origin — the "Google Trick" unified live-search. One
                    field, two result groups: matching HEAR_OPTIONS at
                    the top, matching client names below. Picking either
                    resolves both `hearSource` + optional `referrerId`.
                    IMMUTABILITY RULE (Feb 2026): once picked (or loaded
                    from a prior check-in) the source is LOCKED — no
                    "Change" affordance is rendered. Only Owner/Admin
                    can override from staff settings via the
                    `/customers/{cid}/referral-source` endpoint, and
                    even that never transfers loyalty points. */}
                <div id="checkin-field-hear" className={ringFor("hear")}>
                  <Label htmlFor="hear">How did you hear about us? <span className="text-rose-500">*</span></Label>
                  {hearSource ? (
                    <div className="mt-1 flex items-center gap-2 rounded-md border border-primary/40 bg-primary/5 px-3 py-2 text-sm" data-testid="checkin-hear-selected">
                      <Lock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      <span className="truncate flex-1">
                        <span className="font-medium">{hearLabel}</span>
                      </span>
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground" data-testid="checkin-hear-locked-badge">
                        Locked
                      </span>
                    </div>
                  ) : (
                    <div className="mt-1 relative">
                      <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="hear"
                        value={originQuery}
                        onFocus={() => setOriginOpen(true)}
                        onChange={(e) => { setOriginQuery(e.target.value); setOriginOpen(true); }}
                        placeholder="Type Google, Instagram or a friend's name…"
                        autoComplete="off"
                        className="pl-8"
                        data-testid="checkin-hear"
                      />
                      {originOpen && (
                        <div
                          className="absolute inset-x-0 top-full mt-1 z-20 rounded-md border border-border bg-white shadow-md max-h-64 overflow-auto"
                          data-testid="checkin-hear-results"
                        >
                          {filteredSources.length > 0 && (
                            <>
                              <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/40">Sources</div>
                              {filteredSources.map((h) => (
                                <button
                                  type="button"
                                  key={h.key}
                                  onClick={() => pickSource(h)}
                                  className="w-full text-left px-3 py-2 hover:bg-muted/70 border-b border-border/50 last:border-b-0"
                                  data-testid={`checkin-hear-source-${h.key}`}
                                >
                                  <div className="text-sm font-medium">{h.label}</div>
                                </button>
                              ))}
                            </>
                          )}
                          {originQuery.trim().length >= 2 && (
                            <>
                              <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/40">Referred by a client</div>
                              {originSearching && (
                                <p className="text-xs text-muted-foreground px-3 py-2">Searching…</p>
                              )}
                              {!originSearching && originResults.length === 0 && (
                                <p className="text-xs text-muted-foreground px-3 py-2">No client matches yet — keep typing or pick a source above.</p>
                              )}
                              {originResults.map((r) => (
                                <button
                                  type="button"
                                  key={r.id}
                                  onClick={() => pickCustomer(r)}
                                  className="w-full text-left px-3 py-2 hover:bg-muted/70 border-b border-border/50 last:border-b-0"
                                  data-testid={`checkin-hear-customer-${r.id}`}
                                >
                                  <div className="text-sm font-medium">{r.name}</div>
                                  {r.email && <div className="text-[11px] text-muted-foreground">{r.email}</div>}
                                </button>
                              ))}
                            </>
                          )}
                          {originQuery.trim().length < 2 && (
                            <p className="text-[11px] text-muted-foreground px-3 py-2">Tip: type a friend's name to credit them for the referral.</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Terms & Privacy (Iter-362 · Passwordless Check-In) --- */}
                {isReviewMode ? (
                  <div className="pt-2 border-t border-border/60" data-testid="checkin-security-review-note">
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Sign-in</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      You already have a Cocoon account — no password needed. Whenever you open your guest portal we'll email you a one-time sign-in code.
                    </p>
                  </div>
                ) : (
                  <div className={"pt-2 border-t border-border/60 " + ringFor("terms")} id="checkin-field-terms" data-testid="checkin-terms-block">
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Terms &amp; Privacy <span className="text-rose-500">*</span></p>
                    <p className="text-xs text-muted-foreground mt-2">
                      No password required — Cocoon uses a passwordless sign-in. Whenever you open your guest portal at{" "}
                      <span className="font-medium text-foreground">/portal/login</span> we'll email you a one-time code.
                    </p>
                    <div className="rounded-md border border-border bg-secondary/40 p-3 mt-3 max-h-32 overflow-y-auto text-[12px] leading-relaxed">
                      <p className="font-medium mb-1">Terms &amp; Privacy Policy</p>
                      <p>By ticking the box below you consent to Cocoon Medical Spa collecting your contact + health details to plan and deliver your treatments, and to receive promotional communications about member offers. You can update or withdraw any answer from your portal at any time.</p>
                    </div>
                    <label className="flex items-start gap-2 cursor-pointer select-none mt-2">
                      <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} className="mt-0.5" data-testid="checkin-agree" />
                      <span className="text-sm">I agree to the Cocoon Terms and Privacy Policy, and to receive promotional notifications.</span>
                    </label>
                  </div>
                )}
              </div>
            )}

            {step === 2 && useMassageCard && (
              <MassagePreferenceStep value={massage} onChange={setMassage} />
            )}

            {step === 2 && !useMassageCard && (!skipMedical || isReviewMode) && (
              <div className="space-y-5" data-testid={isReviewMode ? "checkin-step-2-review" : "checkin-step-2"}>
                {isReviewMode ? (
                  <div
                    className="rounded-md border border-teal-200 bg-teal-50 px-3 py-2 text-teal-900"
                    data-testid="checkin-medical-review-header"
                  >
                    <p className="text-sm font-medium">Please review your health details.</p>
                    <p className="text-xs">Update any changes since your last visit.</p>
                  </div>
                ) : (
                  <p className="text-sm">Pre-treatment medical questionnaire — your answers go straight to your therapist.</p>
                )}
                {medicalUpdatedLabel && (
                  <div
                    className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900 flex items-start gap-2"
                    data-testid="checkin-medical-stale-banner"
                  >
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
                    <p className="text-xs leading-snug">
                      Last updated {medicalUpdatedLabel} — please confirm nothing has changed.
                    </p>
                  </div>
                )}
                <div>
                  <Label htmlFor="med-allergies">Medication allergies</Label>
                  <Textarea id="med-allergies" rows={2} value={medical.medication_allergies} onChange={(e) => setMedical((m) => ({ ...m, medication_allergies: e.target.value }))} placeholder="e.g. Penicillin, Sulfa drugs, Latex…" data-testid="checkin-med-allergies" className="mt-1" />
                </div>
                <div>
                  <Label>Cosmetic ingredient allergies</Label>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mt-1.5">
                    {COSMETIC_INGREDIENTS.map((ing) => {
                      const checked = medical.cosmetic_allergies.includes(ing);
                      return (
                        <label key={ing} className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(v) => {
                              const cur = new Set(medical.cosmetic_allergies);
                              if (v) cur.add(ing); else cur.delete(ing);
                              setMedical((m) => ({ ...m, cosmetic_allergies: [...cur] }));
                            }}
                            data-testid={`checkin-cosmetic-${ing.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
                          />
                          <span className="text-xs sm:text-sm">{ing}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <Label htmlFor="cur-meds">Current medications</Label>
                  <Textarea id="cur-meds" rows={2} value={medical.current_medications} onChange={(e) => setMedical((m) => ({ ...m, current_medications: e.target.value }))} placeholder="Prescription + OTC + vitamins…" data-testid="checkin-cur-meds" className="mt-1" />
                </div>
                <div>
                  <Label>Do you have any of these? <span className="text-rose-500">*</span></Label>
                  <p className="text-xs text-muted-foreground mb-1">Every row starts on "No" — tap "Yes" on the ones that apply to you.</p>
                  <div className="mt-2 rounded-md border border-border overflow-hidden">
                    {MEDICAL_QUESTIONS.map((q, i) => {
                      const val = medical.medical_questionnaire?.[q.key] || {};
                      return (
                        <div key={q.key} className={`grid grid-cols-[1fr_auto] gap-2 px-3 py-2 text-sm ${i > 0 ? "border-t border-border/50" : ""}`} data-testid={`checkin-mq-${q.key}`}>
                          <span className="text-xs sm:text-sm leading-snug">{q.label} <span className="text-rose-500">*</span></span>
                          <RadioGroup value={val.answer || ""} onValueChange={(v) => setAnswer(q.key, v)} className="flex gap-3">
                            <label className="flex items-center gap-1 text-xs cursor-pointer"><RadioGroupItem value="yes" data-testid={`checkin-mq-${q.key}-yes`} /> Yes</label>
                            <label className="flex items-center gap-1 text-xs cursor-pointer"><RadioGroupItem value="no" data-testid={`checkin-mq-${q.key}-no`} /> No</label>
                          </RadioGroup>
                          {val.answer === "yes" && (
                            <Input value={val.note || ""} onChange={(e) => setNote(q.key, e.target.value)} placeholder="Add a comment…" className="col-span-2 h-8 mt-1" data-testid={`checkin-mq-note-${q.key}`} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between gap-3 pt-6 mt-2 border-t border-border">
              {isTablet && step === 1 ? (
                <span className="text-xs text-muted-foreground" data-testid="checkin-tablet-notice">Guest tablet · form only</span>
              ) : (
                <Button variant="ghost" onClick={() => (step === 1 ? nav("/") : setStep(step - 1))} disabled={busy} data-testid="checkin-back-btn">
                  {step === 1 ? "Cancel" : "Back"}
                </Button>
              )}
              {step < lastStep ? (
                <Button
                  onClick={goNext}
                  className="rounded-full"
                  data-testid="checkin-next-btn"
                >Continue</Button>
              ) : (
                <Button onClick={submit} disabled={busy || (useMassageCard && !canStep2Massage)} className="rounded-full" data-testid="checkin-finish-btn">
                  {busy ? "Finishing…" : (isReviewMode ? "Submit review & check in" : useMassageCard ? "Submit preferences & check in" : "Finish & activate")}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
